import { useState } from 'react';
import { Activity, TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import {
  backtest,
  computeSignal,
  MIN_POINTS,
  resample,
  TIMEFRAME_MS,
  type Confidence,
  type SignalLevel,
  type Timeframe,
} from '../ai/signals';
import { useChainlinkHistory } from '../web3/useChainlinkHistory';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';

// symbols that have both a Chainlink feed and a *-USDT pool to trade against.
// BUSD is skipped: signals on a stablecoin are meaningless.
const AI_SYMBOLS = ['BNB', 'BTCB', 'ETH'] as const;

export type TradeSide = 'buy' | 'sell';

const LEVEL_META: Record<SignalLevel, { label: string; color: string; bg: string }> = {
  'strong-buy': { label: '강력 매수', color: 'text-tide-400', bg: 'bg-tide-400/10' },
  buy: { label: '매수', color: 'text-tide-400', bg: 'bg-tide-400/10' },
  neutral: { label: '관망', color: 'text-foam-300', bg: 'bg-abyss-700/60' },
  sell: { label: '매도', color: 'text-coral-400', bg: 'bg-coral-500/10' },
  'strong-sell': { label: '강력 매도', color: 'text-coral-400', bg: 'bg-coral-500/10' },
};

const CONFIDENCE_META: Record<Confidence, { label: string; color: string }> = {
  high: { label: '높음', color: 'text-tide-400' },
  medium: { label: '보통', color: 'text-amber-400' },
  low: { label: '낮음', color: 'text-coral-400' },
};

const TIMEFRAMES: Timeframe[] = ['1h', '4h', '1d'];
const TF_LABEL: Record<Timeframe, string> = { '1h': '1시간', '4h': '4시간', '1d': '1일' };

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.round(diff / 60000);
  if (min < 1) return '방금';
  if (min < 60) return `${min}분 전`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  return `${Math.round(hr / 24)}일 전`;
}

export default function AITradingView({
  onTrade,
}: {
  onTrade: (symbol: string, side: TradeSide) => void;
}) {
  const [timeframe, setTimeframe] = useState<Timeframe>('4h');

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
            <Activity size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foam-100">Trading Signals</h2>
            <p className="text-xs text-foam-500 mt-0.5">
              Chainlink 온체인 가격을 등간격으로 리샘플해 RSI · 이동평균 · 모멘텀을 계산한
              규칙 기반 기술적 분석 (머신러닝 아님)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-abyss-700 bg-abyss-850 p-1">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-2.5 h-8 rounded-md text-xs font-semibold transition-colors ${
                timeframe === tf ? 'bg-tide-500 text-abyss-950' : 'text-foam-300 hover:bg-abyss-700'
              }`}
            >
              {TF_LABEL[tf]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {AI_SYMBOLS.map((symbol) => (
          <SignalCard key={symbol} symbol={symbol} timeframe={timeframe} onTrade={onTrade} />
        ))}
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        시그널은 온체인 가격 데이터 기반의 기술적 지표일 뿐, 투자 조언이 아닙니다. 백테스트 성과는
        과거 데이터 기준이며 미래 수익을 보장하지 않습니다. 실행 버튼을 눌러도 거래는 자동으로
        이뤄지지 않으며, 항상 지갑에서 직접 서명해야 체결됩니다. (BSC 테스트넷 데모)
      </div>
      <TrustBar />
    </div>
  );
}

function SignalCard({
  symbol,
  timeframe,
  onTrade,
}: {
  symbol: string;
  timeframe: Timeframe;
  onTrade: (symbol: string, side: TradeSide) => void;
}) {
  const series = useChainlinkHistory(symbol, timeframe);
  const rawPrices = series?.map((p) => p.price) ?? [];

  // resample onto an even grid for statistically valid TA; if the (sparse
  // testnet) feed doesn't yield enough buckets, fall back to raw rounds.
  const resampled = series ? resample(series, TIMEFRAME_MS[timeframe]) : [];
  const prices = resampled.length >= MIN_POINTS ? resampled : rawPrices;

  const signal = series ? computeSignal(prices) : null;
  const bt = series ? backtest(prices) : null;
  const lastPrice = rawPrices.at(-1);
  const lastUpdated = series?.at(-1)?.t;
  // Chainlink rounds older than 3 timeframe buckets are considered stale
  const stale = lastUpdated !== undefined && Date.now() - lastUpdated > TIMEFRAME_MS[timeframe] * 3;

  const isBuyish = signal?.level === 'buy' || signal?.level === 'strong-buy';
  const isSellish = signal?.level === 'sell' || signal?.level === 'strong-sell';
  const chartColor = isSellish ? '#f5484f' : '#22d3a8';

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TokenIcon symbol={symbol} size={28} />
          <div>
            <div className="text-sm font-semibold text-foam-100">{symbol}/USD</div>
            <div className="text-xs text-foam-500 tabular-nums">
              {lastPrice === undefined
                ? '-'
                : `$${lastPrice.toLocaleString('en-US', { maximumFractionDigits: lastPrice < 1 ? 6 : 2 })}`}
            </div>
          </div>
        </div>
        {signal ? (
          <span className={`text-xs font-semibold px-2 py-1 rounded-md ${LEVEL_META[signal.level].color} ${LEVEL_META[signal.level].bg}`}>
            {LEVEL_META[signal.level].label}
          </span>
        ) : (
          <span className="text-[11px] text-foam-500">
            {series === undefined ? '분석 중...' : `데이터 수집 중 (${prices.length}/${MIN_POINTS})`}
          </span>
        )}
      </div>

      {/* data freshness + confidence */}
      {signal && (
        <div className="flex items-center justify-between text-[11px]">
          <span className={stale ? 'text-amber-400' : 'text-foam-500'}>
            {lastUpdated ? `${timeAgo(lastUpdated)} 업데이트` : '—'}
            {stale && ' · 데이터 지연'}
          </span>
          <span className="text-foam-500">
            신뢰도{' '}
            <span className={`font-semibold ${CONFIDENCE_META[signal.confidence].color}`}>
              {CONFIDENCE_META[signal.confidence].label}
            </span>{' '}
            · {signal.dataPoints}p
          </span>
        </div>
      )}

      <div className="h-16 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series ?? []} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={`aiFill-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <YAxis domain={['auto', 'auto']} hide />
            <Area
              type="monotone"
              dataKey="price"
              stroke={chartColor}
              strokeWidth={1.5}
              fill={`url(#aiFill-${symbol})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {signal && (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Indicator
              label="RSI"
              value={signal.rsi.toFixed(0)}
              title="상대강도지수(14). 30 미만 과매도, 70 초과 과매수. Wilder 평활 기준."
            />
            <Indicator
              label="이평 추세"
              value={signal.smaShort > signal.smaLong ? '상승' : '하락'}
              accent={signal.smaShort > signal.smaLong ? 'up' : 'down'}
              title="단기(7)·장기(21) 이동평균 교차. 단기가 장기 위면 상승 추세."
            />
            <Indicator
              label="모멘텀"
              value={`${signal.momentumPct >= 0 ? '+' : ''}${signal.momentumPct.toFixed(2)}%`}
              accent={signal.momentumPct >= 0 ? 'up' : 'down'}
              title="최근 12구간 가격 변화율. 양수면 매수세 우위."
            />
          </div>

          <ul className="text-[11px] text-foam-500 space-y-1">
            {signal.reasons.map((r) => (
              <li key={r} className="flex gap-1.5">
                <span className="text-foam-700">·</span>
                {r}
              </li>
            ))}
          </ul>

          {/* walk-forward backtest summary */}
          {bt && bt.trades > 0 && (
            <div className="rounded-lg border border-abyss-700 bg-abyss-900/60 p-2.5 space-y-1.5">
              <div className="flex items-center justify-between text-[11px] text-foam-500">
                <span title="시그널 대로 매매했을 때의 과거 성과(룩어헤드 없음)">백테스트</span>
                <span>{bt.trades}회 · 승률 {(bt.winRate * 100).toFixed(0)}%</span>
              </div>
              <div className="flex items-center justify-between text-xs tabular-nums">
                <span className={bt.strategyReturnPct >= 0 ? 'text-tide-400' : 'text-coral-400'}>
                  전략 {bt.strategyReturnPct >= 0 ? '+' : ''}{bt.strategyReturnPct.toFixed(2)}%
                </span>
                <span className="text-foam-500">
                  보유 {bt.benchmarkReturnPct >= 0 ? '+' : ''}{bt.benchmarkReturnPct.toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center justify-between text-[11px] text-foam-500 tabular-nums">
                <span title="최대 낙폭">MDD -{bt.maxDrawdownPct.toFixed(1)}%</span>
                <span title="스텝당 위험조정수익(연율화 아님)">Sharpe {bt.sharpe.toFixed(2)}</span>
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-auto grid grid-cols-2 gap-2">
        <button
          onClick={() => onTrade(symbol, 'buy')}
          className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors ${
            isBuyish
              ? 'bg-tide-500 text-abyss-950 hover:bg-tide-400'
              : 'bg-abyss-700 text-foam-300 hover:bg-abyss-600'
          }`}
        >
          <TrendingUp size={13} />
          매수 (USDT→{symbol})
        </button>
        <button
          onClick={() => onTrade(symbol, 'sell')}
          className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors ${
            isSellish
              ? 'bg-coral-500 text-abyss-950 hover:bg-coral-400'
              : 'bg-abyss-700 text-foam-300 hover:bg-abyss-600'
          }`}
        >
          <TrendingDown size={13} />
          매도 ({symbol}→USDT)
        </button>
      </div>
    </div>
  );
}

function Indicator({
  label,
  value,
  accent,
  title,
}: {
  label: string;
  value: string;
  accent?: 'up' | 'down';
  title?: string;
}) {
  return (
    <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700" title={title}>
      <div className="text-[10px] text-foam-500">{label}</div>
      <div
        className={`text-xs font-semibold mt-0.5 tabular-nums ${
          accent === 'up' ? 'text-tide-400' : accent === 'down' ? 'text-coral-400' : 'text-foam-100'
        }`}
      >
        {value}
      </div>
    </div>
  );
}
